import { notification } from '@block/plug';
import { groupEducationByType } from './utils';
import { doAction } from '../../../utils';
import Url from '../url';
import { validateInternTopDegree, validateRegularTopDegree } from './basicValidators';
import { validateInternTopDegreeEndDate } from './weakValidators';
import { EducationTypeDictIndexMap } from '../consts';

/**
 * 暂未使用的校验规则1，防止后续需要开启
 * 校验高学历是否包含本科及以上的所有教育经历
 * @param degreeList 教育经历列表
 * @returns 校验是否通过
 */
export const validateHigherEducationCompleteness = (degreeList) => {
  // 按教育类型分组
  const educationTypeMap = groupEducationByType(degreeList);

  // 检查每种类型的最高学历
  for (const [, sameTypeEducations] of educationTypeMap.entries()) {
    // 找出被标记为最高学历的记录
    const topDegreeItem = sameTypeEducations.find((item) => item.isTopDegree);

    if (topDegreeItem) {
      // 本科及以上学历的diplomaDictIndex阈值（示例值，需根据实际情况调整）
      // 假设diplomaDictIndex >= '68'表示本科及以上
      const bachelorThreshold = '68';

      // 如果最高学历高于本科
      if (topDegreeItem.diplomaDictIndex > bachelorThreshold) {
        // 检查是否包含本科学历
        const hasBachelorDegree = sameTypeEducations.some((item) => item.diplomaDictIndex === bachelorThreshold);

        if (!hasBachelorDegree) {
          notification.error({
            title: '提示',
            message: '教育经历需从本科（大专）开始填写，请补充',
          });
          return false;
        }
      }
    }
  }

  return true;
};

/**
 * 暂未使用的校验规则2，防止后续需要开启
 * @param degreeList - 教育经历数据数组
 * @returns 校验是否通过
 */
export const validateHigherEducationRequirement = (degreeList, isIntern, entryDate) => {
  /**
   * 第三步：按教育类型分组处理教育经历并进行校验
   */
  // 创建一个Map存储按教育类型分组的教育经历
  const educationTypeMap = groupEducationByType(degreeList);

  /**
   * 第四步：根据是否为实习生执行不同的校验逻辑
   */
  // 遍历每个教育类型分组进行校验
  for (const [typeIndex, sameTypeEducations] of educationTypeMap.entries()) {
    // 找出被标记为最高学历的记录
    const topDegreeItem = sameTypeEducations.find((item) => item.isTopDegree);

    // 如果该类型中有被标记为最高学历的记录，进行校验
    if (topDegreeItem) {
      // 获取教育类型名称，用于错误提示
      const typeName = EducationTypeDictIndexMap[typeIndex] || '';

      // 准备校验参数
      const validationParams = {
        topDegreeItem,
        sameTypeEducations,
        typeName,
        entryDate,
      };

      if (!isIntern) {
        // 非实习生校验
        const regularTopDegreeResult = validateRegularTopDegree(validationParams);
        if (!regularTopDegreeResult) {
          return false;
        }
      }
      // 实习生校验
      else {
        // 实习生最高教育经历结束日期校验（弱校验）
        validateInternTopDegreeEndDate(validationParams);

        // 实习生校验
        const internTopDegreeResult = validateInternTopDegree(validationParams);
        if (!internTopDegreeResult) {
          return false;
        }
      }
    }
  }

  return true;
};

/**
 * 验证教育信息
 * @returns {Promise<boolean>} 验证是否通过
 */
export const validateEducationEndDate = async (isIntern, degree) => {
  let status = true;

  // TODO 测试背调是否会触发后端的校验
  // 背调不需要校验
  if (window.checkTypeValue !== 'entry') {
    return status;
  }

  // 实习生不需要校验
  if (isIntern) {
    return status;
  }

  /**
   * 第二步：获取入职日期，用于后续判断是否已毕业
   */

  // 如果提供了自定义入职日期，则使用它；否则使用默认值
  const baseInfoData = await doAction(Url.getJoinDate);
  const { entryDate } = baseInfoData || {}; // 入职日期，可能为空，时间戳格式

  // 不是实习生，需要提示教育经历不能在读
  degree = degree || [];
  for (let i = 0; i < degree.length; i++) {
    const { endDate } = degree[i];
    if (endDate >= entryDate) {
      status = false;
      notification.warning({
        title: '提示',
        message: '请不要填写在读的教育经历',
      });
      break;
    }
  }

  return status;
};

/**
 * 校验多段教育经历的综合逻辑
 *
 * @description
 * 该函数执行一系列校验，确保教育经历数据符合业务规则：
 * 1. 必须至少有一个最高全日制教育经历
 * 2. 根据是否为实习生执行不同的校验逻辑
 * 3. 检查教育经历时间是否有重叠（弱校验）
 * 4. 校验高学历是否包含本科及以上的所有教育经历
 *
 * @param degree - 教育经历数据数组
 * @param isIntern - 是否为实习生
 * @returns 校验是否全部通过
 */
export const validateMultiEducations = async (degree, isIntern) => {
  /**
   * 第一步：校验必须至少要有一个最高全日制教育经历（强校验)改为后端校验
   */
  // const hasFullTimeTopDegreeResult = validateHasFullTimeTopDegree(degree);
  // if (!hasFullTimeTopDegreeResult) {
  //   return false;
  // }

  /**
   * 第二步：校验教育经历不能在读，保留旧版校验规则（强校验)
   */
  const validateEducationStatus = await validateEducationEndDate(isIntern, degree);
  if (!validateEducationStatus) {
    return false;
  }

  /**
   * 第三步：校验教育经历时间是否有重叠（弱校验）
   */
  // validateTimeOverlap函数现在需要三个参数：教育经历列表、当前编辑的教育经历数据和索引
  // 在这里无法提供这些参数，应该在表单编辑时调用此函数
  // validateTimeOverlap(degree);

  // 所有校验都通过，返回true
  return true;
};
